/**
 * DeckPicker — Modal grid of brand-deck pages for picking a costume reference.
 *
 * Used by AssetCard's "Pick from Brand Deck" flow (P1-9). Re-uses the same
 * thumbnail rendering style as BrandDeckPanel so the visual schema is
 * consistent across stages.
 *
 * Props:
 *   open       — boolean
 *   projectId  — string
 *   onPick     — (deckPage) => void  // called with the picked page
 *   onClose    — () => void
 *   isPicking  — boolean // when true, shows a spinner overlay (parent controls it)
 */
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { X, Loader2, ImageIcon } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const resolveUrl = (u) => {
  if (!u) return '';
  if (u.startsWith('http')) return u;
  if (u.startsWith('/')) return `${API}${u}`;
  return u;
};

export default function DeckPicker({ open, projectId, onPick, onClose, isPicking = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    (async () => {
      try {
        const res = await axios.get(`${API}/api/projects/${projectId}/brand-deck`);
        if (!cancelled) setItems(res.data?.items || []);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || e.message || 'Failed to load deck');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, projectId]);

  const filtered = useMemo(() => {
    if (!filter.trim()) return items;
    const q = filter.toLowerCase();
    return items.filter(p =>
      (p.caption || '').toLowerCase().includes(q) ||
      (p.original_filename || '').toLowerCase().includes(q),
    );
  }, [items, filter]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/85"
      onClick={onClose}
      data-testid="deck-picker-modal"
    >
      <div
        className="relative w-full max-w-4xl max-h-[85vh] flex flex-col rounded-xl bg-[#0E1015] border border-white/10 shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div>
            <h3 className="text-sm font-semibold text-white">Pick from Brand Deck</h3>
            <p className="text-[10px] text-white/50 mt-0.5">
              {items.length} page{items.length === 1 ? '' : 's'} uploaded · click a thumbnail to use it as the costume reference
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-colors"
            data-testid="deck-picker-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Filter */}
        {items.length > 6 && (
          <div className="px-4 py-2 border-b border-white/[0.05]">
            <input
              type="text"
              placeholder="Filter by caption or filename…"
              value={filter}
              onChange={e => setFilter(e.target.value)}
              className="w-full bg-white/[0.04] text-white text-[11px] px-3 py-1.5 rounded-lg border border-white/[0.08] outline-none focus:border-white/20"
              data-testid="deck-picker-filter"
            />
          </div>
        )}

        {/* Grid */}
        <div className="flex-1 overflow-auto p-4 relative">
          {isPicking && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/70">
              <Loader2 className="w-8 h-8 animate-spin text-white" />
              <span className="text-xs text-white ml-2">Refining flat-lay…</span>
            </div>
          )}

          {loading && (
            <div className="flex items-center justify-center py-12 text-white/50 text-xs">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading deck pages…
            </div>
          )}

          {!loading && error && (
            <div className="text-center py-8 text-red-400 text-xs">{error}</div>
          )}

          {!loading && !error && items.length === 0 && (
            <div className="text-center py-12 text-white/40 text-xs flex flex-col items-center gap-2">
              <ImageIcon className="w-8 h-8 opacity-30" />
              No deck pages uploaded yet. Upload a brand deck PDF or images first.
            </div>
          )}

          {!loading && !error && filtered.length === 0 && items.length > 0 && (
            <div className="text-center py-8 text-white/40 text-xs">
              No pages match "{filter}".
            </div>
          )}

          {!loading && !error && filtered.length > 0 && (
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {filtered.map(page => (
                <button
                  key={page.id}
                  onClick={() => !isPicking && onPick(page)}
                  disabled={isPicking}
                  className="group relative rounded-lg overflow-hidden border border-white/10 hover:border-ai-accent transition-all bg-white/[0.02] disabled:opacity-50"
                  data-testid={`deck-page-pick-${page.id}`}
                  title={page.caption || page.original_filename || 'Brand deck page'}
                >
                  <div className="aspect-square w-full bg-black/40">
                    <img
                      src={resolveUrl(page.url)}
                      alt={page.caption || `Page ${page.page_number}`}
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="px-2 py-1.5 bg-black/60 text-left">
                    <div className="text-[9px] text-white/80 truncate">
                      {page.caption || `Page ${page.page_number}`}
                    </div>
                    <div className="text-[8px] text-white/40 truncate">
                      {page.original_filename}
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-ai-accent/0 group-hover:bg-ai-accent/10 transition-colors pointer-events-none" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
